/**
 * VideoSection — "Voir Karexia en action". A responsive 16:9 stage with a
 * poster + a play affordance. It NEVER autoplays and NEVER renders a broken
 * embed:
 *   - VIDEO.videoUrl set  → clicking play swaps in a controlled <video> (the
 *                           play is user-initiated, so autoPlay here is allowed).
 *   - VIDEO.videoUrl null → the poster shows with a « bientôt » ribbon and the
 *                           play button is inert.
 * Set VIDEO.videoUrl / VIDEO.posterUrl in landing-data.ts to go live.
 */

import { useState } from 'react';

import { VIDEO } from '@/routes/landing/landing-data';
import { Icon, SectionHeading } from './shared';

export function VideoSection() {
  const [playing, setPlaying] = useState(false);
  const hasVideo = Boolean(VIDEO.videoUrl);

  return (
    <section className="relative overflow-hidden bg-surface-2/40 py-16 sm:py-20 lg:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow={VIDEO.eyebrow} title={VIDEO.title} subtitle={VIDEO.subtitle} />

        <div className="mt-10 overflow-hidden rounded-token-xl border border-border bg-surface shadow-elevation-4">
          <div className="relative aspect-video w-full">
            {playing && VIDEO.videoUrl ? (
              <video
                src={VIDEO.videoUrl}
                poster={VIDEO.posterUrl ?? undefined}
                controls
                autoPlay
                className="h-full w-full bg-black object-contain"
              >
                <track kind="captions" />
              </video>
            ) : (
              <Poster hasVideo={hasVideo} onPlay={() => hasVideo && setPlaying(true)} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Poster({ hasVideo, onPlay }: { hasVideo: boolean; onPlay: () => void }) {
  return (
    <div className="absolute inset-0">
      {/* Gradient poster (or the real poster image when provided). */}
      {VIDEO.posterUrl ? (
        <img src={VIDEO.posterUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <div aria-hidden="true" className="h-full w-full bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600">
          <div className="h-full w-full bg-[radial-gradient(120%_120%_at_20%_0%,rgb(255_255_255/0.22),transparent_55%)]" />
        </div>
      )}

      {/* Play affordance / "bientôt" state */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/20">
        <button
          type="button"
          onClick={onPlay}
          disabled={!hasVideo}
          aria-label={hasVideo ? VIDEO.playLabel : VIDEO.soonLabel}
          className="group inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/95 text-indigo-700 shadow-elevation-3 transition hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-70 motion-reduce:transition-none motion-reduce:hover:scale-100"
        >
          <Icon name="play" className="h-9 w-9 translate-x-0.5 fill-current" strokeWidth={0} />
        </button>
        {!hasVideo && (
          <span className="rounded-full bg-black/40 px-3 py-1 text-sm font-medium text-white backdrop-blur">
            {VIDEO.soonLabel}
          </span>
        )}
      </div>
    </div>
  );
}
